import {
	Scene,
	Vector3,
	MeshBuilder,
	StandardMaterial,
	Color3,
	TransformNode,
	Mesh,
} from "@babylonjs/core";

/**
 * Inventory storage racks visualized as physical 3D objects inside the
 * warehouse. Each row on each rack represents an inventory SKU; the number of
 * stacked rolls/boxes corresponds to how many units the player owns.
 *
 * Visually this is the "this is an inventory sim" tell — like Fulfillment
 * Center Simulator's shelves filling up as orders come in.
 */

export interface StorageRack {
	root: TransformNode;
	setInventory(inv: Record<string, number>): void;
}

const SKU_COLORS: Record<string, Color3> = {
	"filament-pla-1kg": new Color3(0.95, 0.95, 0.95),
	"filament-petg-1kg": new Color3(0.15, 0.15, 0.18),
	"filament-abs-1kg": new Color3(0.85, 0.2, 0.15),
	"filament-tpu-1kg": new Color3(0.95, 0.55, 0.15),
	"nzl-brass-04": new Color3(0.8, 0.65, 0.25),
	"nzl-hardened-06": new Color3(0.5, 0.5, 0.55),
	"thermistor-pt100": new Color3(0.7, 0.2, 0.7),
	"build-plate-pei": new Color3(0.3, 0.4, 0.6),
	"ai-quality-inspector": new Color3(0.3, 1.0, 0.6),
};

const SKU_KIND: Record<string, "roll" | "box"> = {
	"filament-pla-1kg": "roll",
	"filament-petg-1kg": "roll",
	"filament-abs-1kg": "roll",
	"filament-tpu-1kg": "roll",
};

export function buildStorageRack(scene: Scene, origin: Vector3): StorageRack {
	const root = new TransformNode(`rack-${origin.x}-${origin.z}`, scene);
	root.position.copyFrom(origin);

	const frameMat = new StandardMaterial("rack-frame", scene);
	frameMat.diffuseColor = new Color3(0.6, 0.6, 0.65);

	const SHELVES = 4;
	const SHELF_W = 3.6;
	const SHELF_D = 0.6;
	const SHELF_GAP = 0.9;
	const POST_T = 0.08;

	// Posts
	for (const dx of [-1, 1])
		for (const dz of [-1, 1]) {
			const post = MeshBuilder.CreateBox(`rack-post-${dx}-${dz}`, { width: POST_T, depth: POST_T, height: SHELF_GAP * SHELVES + 0.2 }, scene);
			post.position.set(dx * (SHELF_W / 2 - POST_T / 2), (SHELF_GAP * SHELVES) / 2, dz * (SHELF_D / 2 - POST_T / 2));
			post.material = frameMat;
			post.parent = root;
		}

	const shelfAnchors: TransformNode[] = [];
	for (let s = 0; s < SHELVES; s++) {
		const y = (s + 1) * SHELF_GAP - 0.05;
		const shelf = MeshBuilder.CreateBox(`rack-shelf-${s}`, { width: SHELF_W - 0.05, depth: SHELF_D - 0.05, height: 0.05 }, scene);
		shelf.position.y = y;
		shelf.material = frameMat;
		shelf.parent = root;
		const anchor = new TransformNode(`rack-anchor-${s}`, scene);
		anchor.parent = root;
		anchor.position.y = y + 0.025;
		shelfAnchors.push(anchor);
	}

	// One slot per displayable SKU
	const SKUS = Object.keys(SKU_COLORS);
	const visualsBySku = new Map<string, Mesh[]>();

	const setInventory = (inv: Record<string, number>) => {
		for (const m of visualsBySku.values()) m.forEach((x) => x.dispose());
		visualsBySku.clear();

		const sortedSkus = SKUS.filter((sku) => (inv[sku] ?? 0) > 0);
		// Spread SKUs across all shelves so the rack looks "filled" even with
		// few SKU types — 2 SKUs per shelf instead of 4, top-down.
		const slotsPerShelf = 2;
		sortedSkus.forEach((sku, i) => {
			const shelfIdx = Math.min(shelfAnchors.length - 1, Math.floor(i / slotsPerShelf));
			const slotIdx = i % slotsPerShelf;
			const parent = shelfAnchors[shelfIdx]!;

			const qty = Math.min(8, inv[sku] ?? 0);
			const kind = SKU_KIND[sku] ?? "box";
			const color = SKU_COLORS[sku] ?? new Color3(0.7, 0.7, 0.7);
			const mat = new StandardMaterial(`rack-item-${sku}`, scene);
			mat.diffuseColor = color;

			const items: Mesh[] = [];
			for (let q = 0; q < qty; q++) {
				const x = (slotIdx - (slotsPerShelf - 1) / 2) * (SHELF_W / slotsPerShelf);
				let item: Mesh;
				if (kind === "roll") {
					item = MeshBuilder.CreateCylinder(`item-${sku}-${q}`, { diameter: 0.18, height: 0.22, tessellation: 12 }, scene);
					item.position.set(x, 0.11 + (q % 4) * 0.0, 0); // rolls stand upright in a row
					item.position.z = -0.12 + (q % 2) * 0.18;
				} else {
					item = MeshBuilder.CreateBox(`item-${sku}-${q}`, { width: 0.18, depth: 0.18, height: 0.12 }, scene);
					item.position.set(x, 0.06 + Math.floor(q / 2) * 0.13, -0.1 + (q % 2) * 0.18);
				}
				item.material = mat;
				item.parent = parent;
				items.push(item);
			}
			visualsBySku.set(sku, items);
		});
	};

	return { root, setInventory };
}

export function buildPackingStation(scene: Scene, origin: Vector3): { addBox: () => void; reset: () => void } {
	const root = new TransformNode("packing", scene);
	root.position.copyFrom(origin);

	const tableMat = new StandardMaterial("pack-table", scene);
	tableMat.diffuseColor = new Color3(0.35, 0.25, 0.18);
	const table = MeshBuilder.CreateBox("pack-table", { width: 2.2, depth: 1.2, height: 0.06 }, scene);
	table.position.y = 0.9;
	table.material = tableMat;
	table.parent = root;
	for (const dx of [-1, 1])
		for (const dz of [-1, 1]) {
			const leg = MeshBuilder.CreateBox(`pack-leg-${dx}-${dz}`, { width: 0.08, depth: 0.08, height: 0.9 }, scene);
			leg.position.set(dx * 1.0, 0.45, dz * 0.55);
			leg.material = tableMat;
			leg.parent = root;
		}

	const boxMat = new StandardMaterial("pack-box", scene);
	boxMat.diffuseColor = new Color3(0.8, 0.65, 0.35);

	const boxes: Mesh[] = [];
	const addBox = () => {
		const idx = boxes.length;
		const row = Math.floor(idx / 4);
		const col = idx % 4;
		const box = MeshBuilder.CreateBox(`pack-box-${idx}`, { width: 0.4, depth: 0.4, height: 0.3 }, scene);
		box.material = boxMat;
		box.parent = root;
		box.position.set(-0.75 + col * 0.5, 1.1 + row * 0.32, 0);
		boxes.push(box);
		if (boxes.length > 16) {
			const dropped = boxes.shift()!;
			dropped.dispose();
		}
	};

	const reset = () => {
		for (const b of boxes) b.dispose();
		boxes.length = 0;
	};

	return { addBox, reset };
}
